import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { TenantMembership } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/TenantMembership";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { useLazoCompanyByTenantExternalId } from "@/hooks/queries/lazo-company/useLazoCompanyQueries";
import { useLazoTeamOfCompany } from "@/hooks/queries/lazo-team/useLazoTeamQueries";
import { LazoCompany } from "@/models/LazoCompany";
import { WorkspaceAndTenantMemberships } from "@/models/WorkspaceAndTenantMemberships";
import LocalStorageWorkspacesAndTenantMemberships from "@/services/LocalStorageTenantMembership";
import WorkspaceService from "@/services/workspaces/WorkspaceService";
import { LazoTeamOfCompany } from "@/types/LazoTeamOfCompany";
import { LAZO_USER_ROLES } from "@/types/LazoUserRoles";
import { SessionStorageConstants } from "@/types/SessionStorageConstants";
import { Box, Button, Typography } from "@mui/material";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type TenantContextType = {
  currentTenantMembership: TenantMembership;
  setSelectedTenantMembership: (tm: TenantMembership) => void;
  triggerFetch: () => void;
  setSelectedWorkspace: (w: WorkspaceAndTenantMemberships) => void;
  workspaceState: WorkspaceState;
  currentCompany: LazoCompany;
  lazoEmployees: LazoTeamOfCompany[];
};

export const LAZO_TENANT_EXTERNAL_ID = "lazoTenant0";

const TenantContext = createContext<TenantContextType | undefined>(undefined);

type TenantProviderProps = {
  children: React.ReactNode;
};

export type WorkspaceState = {
  workspaces: WorkspaceAndTenantMemberships[];
  selectedWorkspace: WorkspaceAndTenantMemberships;
  selectedTenantMembership: TenantMembership;
  lazoMembership: TenantMembership;
  selectionMemory: {};
  isCompanyAdmin: boolean;
  isLazoAdminOrKam: boolean;
};

export function extractCurrentSlug() {
  const hostArr = window.location.host.split(".");
  if (environment.env !== "dev") {
    if (hostArr.length === 3) {
      return hostArr[0].replaceAll("-qa", "");
    }

    return "";
  }

  if (hostArr.length === 2) {
    return hostArr[0].replaceAll("-dev", "");
  }

  if (hostArr.length === 1) {
    return "";
  }
}

export const WorkspaceEvents = { updateEvent: "WorkspaceUpdateEvent" };

// These are interceptors for the client requests to the server,
// What we do is add the current selected tenant external id to the request
// to provide subscription checking previous to letting the request continue
const registeredInterceptors = [];

export function addHeaderForChart() {
  HttpClientV2.getClient().interceptors.request.use((r) => {
    r.headers.set("X-Tenant-Ext-Id", "lazoTenant0");
    return r;
  });
}

const EMPTY_WORKSPACE_STATE: WorkspaceState = {
  selectedTenantMembership: null,
  workspaces: [],
  selectedWorkspace: null,
  lazoMembership: null,
  selectionMemory: {},
  isCompanyAdmin: false,
  isLazoAdminOrKam: false,
};

type LoadStatus = "loading" | "ready" | "no-membership" | "error";

export enum TenantProviderErrorType {
  ErrorFetchingEmployees,
}

export type TenantProviderError = {
  msg: string;
  type: TenantProviderErrorType;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [workspaceState, setWorkspaceState] = useState<WorkspaceState>(EMPTY_WORKSPACE_STATE);
  const [prevState, setPrevState] = useState<WorkspaceState>({} as WorkspaceState); // For comparisons
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("loading");

  const { data: company } = useLazoCompanyByTenantExternalId(workspaceState.selectedTenantMembership?.tenant?.externalId);
  const { data: lazoEmployees } = useLazoTeamOfCompany(company?.externalId);

  useEffect(() => {
    if (!workspaceState || !workspaceState.selectedTenantMembership) {
      return;
    }

    while (registeredInterceptors.length > 0) {
      var popped = registeredInterceptors.pop();
      HttpClientV2.getClient().interceptors.request.eject(popped);
    }

    registeredInterceptors.push(
      HttpClientV2.getClient().interceptors.request.use((r) => {
        r.headers.set("X-Tenant-Ext-Id", workspaceState.selectedTenantMembership.tenant.externalId);
        return r;
      })
    );
  }, [workspaceState]);

  function fetchWorkspaces() {
    const preSelectTenantExternalId = window.sessionStorage.getItem(SessionStorageConstants.TENANT_EXTERNAL_ID);
    window.sessionStorage.removeItem(SessionStorageConstants.TENANT_EXTERNAL_ID);

    WorkspaceService.getWorkspacesAndTenantMembershipsForUser().then((r) => {
      r.onSuccess((rawContent) => {
        // Sanitize: drop memberships without a valid tenant so downstream code can trust the shape.
        const content = (rawContent ?? [])
          .map((w) => ({
            ...w,
            memberships: (w?.memberships ?? []).filter((m) => !!m?.tenant?.externalId),
          }))
          .filter((w) => w.memberships.length > 0);

        if (content.length === 0) {
          setLoadStatus("no-membership");
          return;
        }

        const slug = extractCurrentSlug();

        // Find the workspace using the slug. If no workspace matches, choose the first element from the array.
        const foundCurrentWorkspace = Math.max(
          content.findIndex((w) => w.workspace.slug === slug),
          0
        );
        const currentWorkspace = content[foundCurrentWorkspace];
        if (!currentWorkspace || !currentWorkspace.memberships || currentWorkspace.memberships.length === 0) {
          setLoadStatus("no-membership");
          return;
        }

        const lazoIdx = content.findIndex((w) => w.memberships.filter((m) => m.tenant.externalId === "lazoTenant0"));
        let lazoMembership: TenantMembership = null;
        if (lazoIdx !== -1) {
          lazoMembership = content[lazoIdx].memberships.find((m) => m.tenant.externalId === "lazoTenant0")!;
        }

        // In case we come from another workspace use the provided tenant external id to
        // find the membership
        let foundPreSelectedTenantMembership = currentWorkspace.memberships[0];
        if (preSelectTenantExternalId) {
          foundPreSelectedTenantMembership =
            currentWorkspace.memberships.find((m) => m.tenant.externalId === preSelectTenantExternalId) ?? currentWorkspace.memberships[0];
        }

        // Find the matching membership from fresh API data instead of reusing stale prevState
        const previousTenantExtId = prevState.selectedTenantMembership?.tenant?.externalId;
        const freshSelectedMembership = previousTenantExtId
          ? (currentWorkspace.memberships.find((m) => m.tenant.externalId === previousTenantExtId) ?? currentWorkspace.memberships[0])
          : foundPreSelectedTenantMembership;

        const state = {
          lazoMembership: lazoMembership,
          workspaces: content,
          selectedWorkspace: currentWorkspace,
          selectedTenantMembership: freshSelectedMembership,
          selectionMemory: {
            [currentWorkspace.workspace.slug]: currentWorkspace.memberships[0].tenant.externalId,
          },
          isCompanyAdmin: currentWorkspace.memberships[0].roles?.some((r) => r.name === LAZO_USER_ROLES.COMPANY_USER_ADMIN),
          isLazoAdminOrKam:
            lazoMembership == null
              ? false
              : lazoMembership.roles?.some(
                  (r) =>
                    r.name === LAZO_USER_ROLES.LAZO_PORTAL_USER ||
                    r.name === LAZO_USER_ROLES.LAZO_PORTAL_KAM ||
                    r.name === LAZO_USER_ROLES.LAZO_TEAM_ADMIN
                ),
        };

        updateState(state);
        setLoadStatus("ready");
      }).onError((m) => {
        console.log("There was an error getting the user's Workspaces. Error: " + m);
        setLoadStatus("error");
      });
    });
  }

  function updateState(state: WorkspaceState) {
    // Check if changed workspace
    if (JSON.stringify(prevState.selectedWorkspace) !== JSON.stringify(state.selectedWorkspace)) {
      const membershipExtId = state.selectionMemory[state.selectedWorkspace.workspace.slug];

      // If we don't have memory about this space then select the first membership
      if (!membershipExtId) {
        state.selectionMemory[state.selectedWorkspace.workspace.slug] = state.selectedWorkspace.memberships[0].tenant.externalId;
        state.selectedTenantMembership = state.selectedWorkspace.memberships[0];

        // ... else just select the saved membership
      } else {
        state.selectedTenantMembership = state.selectedWorkspace.memberships.find((m) => m.tenant.externalId === membershipExtId)!;
      }

      // Check if changed membership and save to memory
    } else if (JSON.stringify(prevState.selectedTenantMembership) !== JSON.stringify(state.selectedTenantMembership)) {
      state.selectionMemory[state.selectedWorkspace.workspace.slug] = state.selectedTenantMembership.tenant.externalId;
    }
    state.isCompanyAdmin = state.selectedTenantMembership.roles.some((r) => r.name === LAZO_USER_ROLES.COMPANY_USER_ADMIN);
    state.isLazoAdminOrKam =
      state.lazoMembership == null
        ? false
        : state.lazoMembership.roles.some(
            (r) =>
              r.name === LAZO_USER_ROLES.LAZO_PORTAL_USER ||
              r.name === LAZO_USER_ROLES.LAZO_PORTAL_KAM ||
              r.name === LAZO_USER_ROLES.LAZO_TEAM_ADMIN
          );

    setPrevState(state);
    setWorkspaceState(state);

    LocalStorageWorkspacesAndTenantMemberships.putWorkspaceState(state);

    document.dispatchEvent(new CustomEvent(WorkspaceEvents.updateEvent, { detail: state }));
  }

  useMemo(() => {
    const savedState = LocalStorageWorkspacesAndTenantMemberships.getWorkspacesState();
    //we need to check this if the users has logged from another user/workspace. It corenr but might generate issues
    if (savedState?.selectedTenantMembership?.tenant?.externalId) {
      setWorkspaceState(savedState);
      setLoadStatus("ready");
    }
    // Always fetch fresh data from API to ensure roles and memberships are up to date
    fetchWorkspaces();
  }, []);

  const contextValue: TenantContextType = useMemo(() => {
    return {
      setSelectedTenantMembership: (m) => updateState({ ...workspaceState, selectedTenantMembership: m }),
      setSelectedWorkspace: (w) => updateState({ ...workspaceState, selectedWorkspace: w }),
      currentTenantMembership: workspaceState.selectedTenantMembership,
      workspaceState: workspaceState,
      triggerFetch: fetchWorkspaces,
      currentCompany: company,
      lazoEmployees,
    };
  }, [updateState, workspaceState, workspaceState?.selectedTenantMembership, fetchWorkspaces, company]);

  if (loadStatus === "loading") {
    return (
      <Box sx={{ width: "100%", height: "100vh" }}>
        <Spinner sx={{ color: Colors.LAZO_PURPLE }} />
      </Box>
    );
  }

  if (loadStatus === "no-membership" || loadStatus === "error") {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <Typography variant="h5" sx={{ color: Colors.BLACK_151517, fontWeight: 600 }}>
          {loadStatus === "no-membership" ? "No workspace access" : "We couldn't load your workspaces"}
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.TEXT_SECONDARY, maxWidth: "32rem" }}>
          {loadStatus === "no-membership"
            ? "You don't have access to any workspace yet. Please contact your administrator or Lazo support."
            : "Please refresh the page. If the problem persists, contact Lazo support."}
        </Typography>
        {loadStatus === "error" && (
          <Button variant="contained" onClick={() => window.location.reload()} sx={{ marginTop: "0.5rem" }}>
            Refresh
          </Button>
        )}
      </Box>
    );
  }

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
};

export const useTenantMemberships = (): TenantContextType => {
  const context = useContext(TenantContext);
  return context!;
};
